import React from 'react';
import { ACTION_SHEET_BORDER_RADIUS } from '@/components/base-action-sheet';
import { Animated, Platform, Text, View, ViewStyle } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { FilterType } from '@/components/filter-button';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import {
  HeaderListOption,
  TITLE_PADDING_TOP,
  useItemDetailTitleAnimation,
  useItemDetailTitleMaxHeight,
  useRenderInDraggableSheet,
} from '@/utils/item-details';
import { HorizontalSelector } from '@/components/horizontal-selector';
import { useLayoutContext } from '@/contexts/layout-context';
import { useScrollViewTabsContext } from '@/contexts/scroll-view-tabs-context';

interface Props {
  isVisible: boolean;
  ItemMenuButton: React.ReactNode;
  onVisibilityChange: (nextValue: boolean) => void;
  title: string;
}

const MINIMUM_VISIBLE_TABS = 3;

export const ItemDetailTitle: React.FC<Props> = ({
  isVisible,
  ItemMenuButton,
  onVisibilityChange,
  title,
}) => {
  const { tabOptions, selectedTabOption, onTabPress } =
    useScrollViewTabsContext();
  const { isSmallLayout } = useLayoutContext();
  const [styles] = useStyles();
  const maxHeight = useItemDetailTitleMaxHeight();
  const isInDraggableSheet = useRenderInDraggableSheet();

  const { headerOpacityRef, headerTranslateYRef } = useItemDetailTitleAnimation(
    isVisible,
    onVisibilityChange,
  );

  if (tabOptions.length < MINIMUM_VISIBLE_TABS) {
    return null;
  }

  return (
    <Animated.View
      pointerEvents={isVisible ? 'auto' : 'none'}
      style={[
        styles.base,
        {
          maxHeight,
          opacity: headerOpacityRef.current,
          transform: [{ translateY: headerTranslateYRef.current }],
        },
      ]}
    >
      {isSmallLayout ? (
        <View
          style={[
            styles.titleSection,
            isInDraggableSheet ? null : styles.titlePaddingTop,
          ]}
        >
          <Text numberOfLines={1} style={[TYPOGRAPHY.medium, styles.title]}>
            {title}
          </Text>
          {ItemMenuButton}
        </View>
      ) : null}
      <View style={styles.selectorWrapper}>
        <HorizontalSelector<HeaderListOption>
          contentContainerStyle={styles.listContainer}
          data={tabOptions}
          filterType={FilterType.TAB}
          hideFooterSpacing
          keyExtractor={(item) => String(item.value)}
          onItemPress={onTabPress}
          scrollToItem
          selectedItem={selectedTabOption}
          trackingKey="item-detail-sections"
        />
      </View>
    </Animated.View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: ACTION_SHEET_BORDER_RADIUS,
    borderTopRightRadius: ACTION_SHEET_BORDER_RADIUS,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: 1,
  },
  listContainer: {
    marginLeft: 0,
  },
  selectorWrapper: {
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    ...Platform.select<ViewStyle>({
      android: {
        borderBottomColor: theme.colors.stroke,
        borderBottomWidth: 1,
      },
      default: {
        ...shadow({
          elevation: 0,
          opacity: 0.1,
          radius: 4,
          x: 0,
          y: 4,
        }),
      },
    }),
  },
  title: {
    ...FONTS.primarySemiBold,
    flexGrow: 1,
    flexShrink: 1,
    paddingHorizontal: 16,
    textAlign: 'center',
  },
  titlePaddingTop: {
    paddingTop: TITLE_PADDING_TOP,
  },
  titleSection: {
    flexDirection: 'row',
    marginBottom: 16,
  },
}));
